
import { useEffect, useRef, useState, useMemo } from 'react'
import { group } from 'd3-array'
import { useLockBodyScroll, useWindowSize } from 'react-use'
import Map from '../Map/'
import './styles.scss'
import { useNavigate, Link, useParams } from 'react-router-dom'
import backChevron from '../images/backChevron.svg'
import share from '../images/share.svg'
import classNames from 'classnames'
import { useStorage, useStorageDownloadURL } from 'reactfire'
import { ref } from 'firebase/storage'
import useMapDimensions from '../hooks/useMapDimensions'
import { useInView } from 'react-intersection-observer';
import Modal from '../Modal/'
import ShareModal from '../Share'
const viewByOptions = ['park', 'grove']

function groveCoordinateNumber(value) {
  if (value === null || value === undefined || value === '') return NaN
  return typeof value === 'number' ? value : parseFloat(String(value).trim())
}

function groveHasValidCoordinates(grove) {
  if (!grove) return false
  const lat = groveCoordinateNumber(grove.lat)
  const lng = groveCoordinateNumber(grove.lng)
  return Number.isFinite(lat) && Number.isFinite(lng)
}

const FullScreenImage = (props) => {
  const { close, image, caption } = props

  const targetImageWidth = useWindowSize().width / 2
  useLockBodyScroll(true)
  return (
    <div className='FullScreenImage' onClick={() => close()}>
      <Image targetImageWidth={targetImageWidth} {...props} {...image } click={() => close()} />
      <div className='caption'>{caption}</div>
      <div className='closeButton' onClick={() => close()}>&times;</div>
    </div>
  )
}
const Image = (props) => {
  const { orientation, dimensions, translate, scale, caption, name, click, targetImageWidth} = props
  const targetAspectRatio = 3 / 2

  const targetImageHeight = targetImageWidth * targetAspectRatio

  const imageHeight = orientation === 'portrait' ? targetImageHeight : targetImageWidth
  const imageWidth = orientation === 'portrait' ? targetImageWidth : targetImageHeight

  const initialScaleFactor = Math.max(imageWidth / dimensions.width, imageHeight / dimensions.height) * 100


  const storage = useStorage()
  const imageRef = ref(storage, `grove-images/${name}`)
  const { data: imageURL } = useStorageDownloadURL(imageRef);
  let path = imageURL
  return (
    <div style={{
      width: imageWidth,
      height: imageHeight,
      backgroundImage: `url("${path}")`,
      // backgroundSize: `${dimensions.width / imageWidth * 50 * tempScale}% ${dimensions.height / imageHeight * 50* tempScale}%`,
      backgroundSize: `${(dimensions.width / imageWidth ) * scale * initialScaleFactor}% ${( dimensions.height / imageHeight) * scale * initialScaleFactor}%`,
      backgroundPosition: `${50 - translate[0]}% ${50 - translate[1]}%`,

    }}
    className='image'
    title={caption}
    onClick={click}
    />
  )
}
const Detail = (props) => {
  const { grove, park } = props
  const { name, yearDedicated, dedicatedBy, images, ancestralLand, locationEstimation, backgroundStory } = grove
  const [galleryViewImageIndex, setGalleryViewImageIndex] = useState(null)
  const clickImage = (index) => e => {
    setGalleryViewImageIndex(index)
  }

  const [shareModalOpen, setShareModalOpen] = useState(false)
  const openShareModal = (e) => {
    setShareModalOpen(true)
    e.preventDefault()
  }
  const modal = shareModalOpen ?
    <Modal modal={{component: <ShareModal grove={grove} />, label: 'Share'}} toggleModal={() => setShareModalOpen(false)} />
    : null
  const locationDisclaimer = !groveHasValidCoordinates(grove) ?
    <div className='locationDisclaimer'>This grove is missing precise location information.</div> :
    null
  const estimationDisclaimer = locationEstimation ? <div className='locationDisclaimer'>Grove location may not be exact. Contact <a href="mailto:jperkins@savetheredwoods.org">Jackie Perkins</a> to plan your visit.</div> : null

  return (
    <>
      <div className='Detail'>
        <div className='buttons'>
          <Link to='/find-a-grove'>
            <img src={backChevron} alt='back to list view' />
            Back to list view</Link>
          <a href="#share" onClick={openShareModal}>
            <img src={share} alt='share grove' />
            Share this Grove</a>
        </div>
        <h2>{name}</h2>
        { park && park.name ? <div className='fact'>
          <div className='fact-title'>Park</div>
          <div className='fact-value'>{park.name}</div>
        </div> : null }
        {locationDisclaimer}
        {estimationDisclaimer}

        { yearDedicated ? <div className='fact'>
          <div className='fact-title'>Year dedicated</div>
          <div className='fact-value'>{yearDedicated}</div>
        </div> : null }
        { dedicatedBy ? <div className='fact'>
          <div className='fact-title'>Dedicated by</div>
          <div className='fact-value'>{dedicatedBy}</div>
        </div> : null }
        { ancestralLand ? <div className='fact'>
          <div className='fact-title'>Traditional Indigenous Land</div>
          <div className='fact-value'>{ancestralLand}</div>
        </div> : null }
        { backgroundStory ? <div className='fact'>
          <div className='fact-title'>Background</div>
          <div className='fact-value text'>{backgroundStory}</div>
        </div> : null }
        { images && images.length ? <div className='fact'>
          <div className='fact-title'>Photos</div>
          <div className='fact-value images'>{images.map((img, index) => <div className='Image' key={img.name}><Image targetImageWidth={200} click={clickImage(index)} key={img.name} {...img} /></div>)}</div>
        </div> : null }
        { galleryViewImageIndex !== null ? <FullScreenImage close={e => setGalleryViewImageIndex(null)} image={images[galleryViewImageIndex]} /> : null }
      </div>
      {modal}
    </>
  )
}

const LetterGroup = (props) => {
  const { letter, data, selectedPark, viewBy, debouncedClickLetter, setHoveredListItem} = props
  const { ref, inView } = useInView({
    threshold: 0,
  })
  useEffect(() => {
    if (inView) {
      debouncedClickLetter({letter, f: 'add'})
    } else {
      debouncedClickLetter({letter, f: 'remove'})
    }
  }, [inView, letter, debouncedClickLetter])

  return (
    <div className='list-group' ref={ref} >
      <div className='list-group-header' id={`letter_${letter}`}>{letter}</div>
      <div className='list-group-items'>
        {data.map(item => {
          let text = item.name
          let selected = item === selectedPark
          if (viewBy === 'grove') {
            text = <Link to={`/find-a-grove/${item.slug}`}>
              {text}

              {item.backgroundStory ? <div className='backgroundStoryIcon icon' /> : null}
              {item.images && item.images.length ? <div className='imagesIcon icon' /> : null}
            </Link>
          } else {
            text = <Link to={`/find-a-grove/park/${item.id}`}>{text}</Link>
          }

          return (
            <div
              key={item.id}
              className={classNames('list-group-item', { selected })}
              onMouseOver={() => setHoveredListItem(item)}
              onMouseOut={() => setHoveredListItem(null)}
              onClick={() => setHoveredListItem(null)}
            >
              {text}
            </div>
          )
        })}
      </div>
      {/* <LetterHeader letter={letter} setClickedLetter={debouncedClickLetter} clickedLetter={clickedLetter} /> */}

    </div>

  )
}

const List = (props) => {
  const { listRef, listPanelRef, dataset, viewBy, selectedPark, setHoveredListItem, stackMaps, clickedLetter, setClickedLetter } = props
  const dataByFirstLetter = Array.from(group(dataset, d => {
    const firstChar = d.name.charAt(0).toUpperCase()
    //check if first char is A-Z with regular expression
    if (/[A-Z]/.test(firstChar)) {
      return firstChar
    }
    return 'other'
  }))
  dataByFirstLetter.forEach(letterGroup => {
    letterGroup[1].sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
  })
  dataByFirstLetter.sort((a, b) => {
    if (a[0] === 'other' && b[0] === 'other') {
      return 0
    } else if (a[0] === 'other') {
      return 1
    } else if (b[0] === 'other') {
      return -1
    }
    return a[0].localeCompare(b[0])
  })

  const validLetters = dataByFirstLetter.map(d => d[0])

  const allLetters = []
  for (let i = 'A'.charCodeAt(0); i <= 'Z'.charCodeAt(0); i++) {
    allLetters.push(String.fromCharCode(i))
  }
  allLetters.push('other')
  const disableDebouncedSet = useRef(false)
  const clickLetter = (letter) => e => {
    const ele = document.querySelector(`#letter_${letter}`)
    if (ele && listRef.current && listPanelRef.current) {
      const list = stackMaps ? listPanelRef.current : listRef.current
      const offset = ele.offsetTop - list.offsetTop
      list.scroll({ top: offset, behavior: 'smooth' })
      setClickedLetter(letter)
      disableDebouncedSet.current = true
    }
  }
  const letterClicks = useRef([])
  const debouncedClickLetterTimeout = useRef(null)
  const debouncedClickLetter = (letter) => {
    if (letter.f === 'add') {
      letterClicks.current.push(letter)
    } else if (letter.f === 'remove') {
      letterClicks.current = letterClicks.current.filter(l => l.letter !== letter.letter)
    }
    clearTimeout(debouncedClickLetterTimeout.current)
    debouncedClickLetterTimeout.current = setTimeout(() => {
      if (disableDebouncedSet.current) {
        disableDebouncedSet.current = false
        return
      }
      const letters = [...letterClicks.current]
      letters.sort((a, b) => a.letter.localeCompare(b.letter))
      if (letters.length) {
        setClickedLetter(letters[0].letter)
      }
      letterClicks.current = []


    }, 300)

  }
  return (
    <>
      <div className='letterSelector'>
        {allLetters.map(letter => {
          const isValid = validLetters.includes(letter)
          return (
            <div
              className={`letter ${isValid ? 'valid' : 'invalid'} ${letter === clickedLetter ? 'active': ''}`}
              key={letter}
              onClick={clickLetter(letter)}
            >
              {letter}
            </div>
          )
        })}
      </div>

      <div className='list' ref={listRef}>
        {dataByFirstLetter.map(([letter, data]) => (
          <LetterGroup
            letter={letter}
            data={data}
            key={letter}
            selectedPark={selectedPark}
            viewBy={viewBy}
            debouncedClickLetter={debouncedClickLetter}
            setHoveredListItem={setHoveredListItem}
          />
        ))}
      </div>
    </>
  )
}


export default function FindAGrove(props) {
  const { groveData } = props
  const grovesReady = groveData && groveData.groves
  const [mapLoaded, setMapLoaded] = useState(false)

  const { mapWidth, mapHeight, stackMaps } = useMapDimensions()

  const dateUpdated = groveData ? new Date(groveData.dateUpdated).toLocaleDateString() : ''

  const [search, setSearch] = useState('')
  const [viewBy, setViewBy] = useState(viewByOptions[1])

  const [hoveredListItem, setHoveredListItem] = useState(null)
  const routeParams = useParams()

  let list = null
  const listRef = useRef()
  const listPanelRef = useRef()
  const navigate = useNavigate()
  let showDetail = false
  const [clickedLetter, setClickedLetter] = useState('A')

  useEffect(() => {
    window.document.body.classList.remove('hideTitle')
  }, [])

  const setView = (view) => () => {
    setViewBy(view)
    if (view === 'grove') {
      navigate('/find-a-grove')
    }
    setClickedLetter('A')
    if (listRef.current) {
      const list = listRef.current
      list.scroll({ top: 0 })
    }
    if (listPanelRef.current) {
      listPanelRef.current.scroll({ top: 0 })
    }

  }

  const { routeHasGrove, routeHasPark, parks } = useMemo(() => {
    let routeHasGrove = null
    let routeHasPark = null
    let parks = []
    if (grovesReady) {
      const parksIdsWithData = Array.from(new Set(groveData.groves.map(grove => grove.park)))
      parks = groveData.parks.filter(park => parksIdsWithData.includes(park.id))

      routeHasGrove = routeParams.slug ? groveData.groves.find(grove => grove.slug === routeParams.slug) : null
      routeHasPark = routeParams.parkId ? parks.find(park => park.id === routeParams.parkId) : null
    }
    return { routeHasGrove, routeHasPark, parks }
  }, [groveData, grovesReady, routeParams.parkId, routeParams.slug])

  useEffect(() => {
    if (routeHasPark && viewBy === 'grove') {
      setViewBy('park')
    }
  }, [routeHasPark, viewBy])
  let selectedPark = null
  if (grovesReady) {
    let dataset = viewBy === 'park' ? parks : groveData.groves
    if (search) {
      dataset = dataset.filter(d => d.name.toLowerCase().includes(search.toLowerCase()))
    }
    selectedPark = routeHasPark
    if (!routeHasGrove) {
      list = <>

        <div className='lastUpdated'>Latest Update {dateUpdated}</div>
        <h1>Find A Grove</h1>
        <div className='searchWrapper'>
          <input size={40} type="text" placeholder="Search by Grove, Donor or Park" className='search' value={search} onChange={e => setSearch(e.target.value)} />
          {search ? <div className='clearSearch' onClick={() => setSearch('')}>&times;</div> : null}
        </div>
        <div className='viewBy'>
          <div className={viewBy === 'park' ? 'selected' : ''} onClick={setView('park')}>Park</div>
          <div className={viewBy === 'grove' ? 'selected' : ''} onClick={setView('grove')}>Grove</div>
        </div>
        <List
          stackMaps={stackMaps}
          setHoveredListItem={setHoveredListItem}
          dataset={dataset}
          listRef={listRef}
          listPanelRef={listPanelRef}
          viewBy={viewBy}
          selectedPark={routeHasPark}
          setClickedLetter={setClickedLetter}
          clickedLetter={clickedLetter}
        />
      </>

    } else {
      showDetail = true
      const park = parks.find(park => park.id === routeHasGrove.park)
      list = <Detail grove={routeHasGrove} park={park}/>
    }
  }

  return (
    <div className='FindAGrove'>
      <div className={ classNames('FindAGroveContent', { stackMaps })}>
        <Map
          groveData={groveData}
          width={mapWidth}
          height={mapHeight}
          mapLoaded={mapLoaded}
          setMapLoaded={setMapLoaded}
          groves={groveData ? groveData.groves : []}
          interactable
          selectedGroveSlug={routeParams.slug}
          selectedPark={selectedPark}
          hoveredListItem={hoveredListItem}
        />
        <div className='listPanel' ref={listPanelRef} style={{ height: showDetail ? null : mapHeight }}>

          {list}
        </div>
      </div>
    </div>
  )
}
